import { useCallback } from "react"

import { Handle, Position, useReactFlow } from "@xyflow/react"

import type { NodeProps } from "@xyflow/react"

import { Merge, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

import { useNodeExecutionState } from "../../hooks/use-node-execution-state"
import { useWorkflowStore } from "../../stores/workflow-store"
import { NodeExecutionIndicator } from "../node-execution-indicator"
import { NodeOutputPopover } from "../node-output-popover"
import { NodeEditDialog } from "./node-edit-dialog"

export function MergeNode({ id, data, selected }: NodeProps) {
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData)
  const { deleteElements } = useReactFlow()
  const { isExecuting, isCompleted, output } = useNodeExecutionState(id)

  const handleStrategyChange = useCallback(
    (value: string) => {
      updateNodeData(id, { strategy: value })
    },
    [id, updateNodeData]
  )

  const handleAggregateModeChange = useCallback(
    (value: string) => {
      updateNodeData(id, { aggregateMode: value })
    },
    [id, updateNodeData]
  )

  const handleDelete = useCallback(() => {
    void deleteElements({ nodes: [{ id }] })
  }, [id, deleteElements])

  const strategy = (data.strategy as string) || "all"
  const aggregateMode = (data.aggregateMode as string) || "concat"
  const hasOutput = isCompleted && !!output
  const renderEditor = () => (
    <div className="grid gap-2">
      <div className="grid gap-1.5">
        <Label className="text-xs">Merge Strategy</Label>
        <Select value={strategy} onValueChange={handleStrategyChange}>
          <SelectTrigger className="nodrag h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Wait for all</SelectItem>
            <SelectItem value="any">Continue on any</SelectItem>
            <SelectItem value="first">Use first result</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {strategy === "all" && (
        <div className="grid gap-1.5">
          <Label className="text-xs">Aggregate Mode</Label>
          <Select value={aggregateMode} onValueChange={handleAggregateModeChange}>
            <SelectTrigger className="nodrag h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="concat">Concatenate results</SelectItem>
              <SelectItem value="json">Return as JSON</SelectItem>
              <SelectItem value="llm">Summarize with AI</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="text-muted-foreground text-center text-[10px]">Joins parallel branches</div>
    </div>
  )

  return (
    <NodeOutputPopover disabled={!hasOutput} output={output || ""}>
      <Card
        className={cn(
          "relative min-w-[180px] border-2 shadow-md transition-[border-color,box-shadow] duration-200",
          data.active ? "border-success ring-success ring-2" : "border-border",
          selected && "ring-primary ring-offset-background ring-2 ring-offset-2",
          isExecuting && "workflow-node-executing",
          isCompleted && !isExecuting && "workflow-node-completed",
          hasOutput && "cursor-pointer"
        )}
      >
        <CardHeader className={cn("rounded-t-xl p-2", CATEGORY_TONES[3])}>
          <NodeExecutionIndicator
            hasOutput={hasOutput}
            isCompleted={isCompleted}
            isExecuting={isExecuting}
          />
          <CardTitle className="flex items-center justify-center gap-2 text-sm font-medium">
            <Merge className="size-4" />
            Merge
            <NodeEditDialog description="Edit merge behavior." title="Merge settings">
              {renderEditor()}
            </NodeEditDialog>
          </CardTitle>
          {selected && (
            <Button
              className="nodrag absolute -top-2.5 -right-2.5 size-6 rounded-full p-0 shadow-md"
              size="icon"
              variant="destructive"
              onClick={handleDelete}
            >
              <X className="size-3" />
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-3">
          {renderEditor()}
          <Handle position={Position.Left} type="target" />
          <Handle position={Position.Right} type="source" />
        </CardContent>
      </Card>
    </NodeOutputPopover>
  )
}
